import React, { useState, useRef } from 'react'
import ReactDOM from 'react-dom'
import map from 'lodash/map'
import classnames from 'classnames'
import AddGuideline from '@Images/project_setup_form_add_guideline.svg'
import Checkmark from '@Images/guidelines_dropdown_checked.svg'
import useTooltipDelay from '@Utils/UseTooltipDelay'
import useClickOutsideListener from '@Utils/UseClickOutsideListener'
import Tooltip from '@SiteComponents/Tooltip'

import {
  IGuideline,
} from '@SiteModules/Guideline/types'

import './GuidelinesDropdown.scss'

const DROPDOWN_OFFSET = 5 // px
const TOOLTIP_OFFSET = 13 // px
const TOOLTIP_WIDTH = 234 // px from SetupProjectPopup__tooltip in SetupProjectPopup.scss

interface GuidelinesDropdownProps {
  readonly guidelines: IGuideline[]
  readonly guidelineOption: string | null
  setGuidelineOption(newGuidelineOption: string | null): void
}

const GuidelinesDropdown: React.FC<GuidelinesDropdownProps> = props => {
  const {
    guidelines,
    guidelineOption,
  } = props

  const [showGuidelinesDropdown, setShowGuidelinesDropdown] = useState(false)

  const guidelineButtonRef = useRef<HTMLDivElement | null>(null)

  useClickOutsideListener('GuidelinesDropdown__element', setShowGuidelinesDropdown, showGuidelinesDropdown)

  const noGuidelineSelected = !guidelineOption
  const toolTipDelayHookGuideline = useTooltipDelay()
  const shouldShowTooltip = toolTipDelayHookGuideline.showTooltip && !showGuidelinesDropdown

  const toggleGuidelinesDropdown = () => {
    setShowGuidelinesDropdown(!showGuidelinesDropdown)

    if (!showGuidelinesDropdown) {
      toolTipDelayHookGuideline.cancelTooltip()
    }
  }

  const onMouseEnterGuideline = () => {
    toolTipDelayHookGuideline.triggerShowTooltip()
  }

  const onMouseLeaveGuideline = () => {
    toolTipDelayHookGuideline.cancelTooltip()
  }

  const handleSetNoGuidelineOption = () => {
    if (noGuidelineSelected) { return }

    props.setGuidelineOption(null)
    setShowGuidelinesDropdown(false)
  }

  const sortedGuidelines= [...guidelines].sort((a, b) => {
    if (a.attributes.name === guidelineOption) { return -1 }
    if (b.attributes.name === guidelineOption) { return 1 }
    return 0
  })

  const guidelinesList = map(sortedGuidelines, (guideline, index) => {
    const isSelected = guideline.attributes.name === guidelineOption

    const handleSetGuidelineOption = () => {
      if (isSelected) { return }

      props.setGuidelineOption(guideline.attributes.name)
      setShowGuidelinesDropdown(false)
    }

    return (
      <li
        key={index}
        className={classnames(
          'GuidelinesDropdown__item',
          { 'GuidelinesDropdown__item-selectable': !isSelected }
        )}
        onClick={handleSetGuidelineOption}
      >
        <div className="GuidelinesDropdown__item-name">
          {guideline.attributes.name}
        </div>
        {isSelected && (
          <Checkmark />
        )}
      </li>
    )
  })

  const renderGuidelinesDropdown = () => {
    const guidelineButtonBounds = guidelineButtonRef.current ? guidelineButtonRef.current.getBoundingClientRect() : { x: 0, y: 0, width: 0, height: 0 }
    const dropdownY = guidelineButtonBounds.y + guidelineButtonBounds.height + DROPDOWN_OFFSET
    const dropdownX = guidelineButtonBounds.x

    const style = {
      top: `${dropdownY}px`,
      left: `${dropdownX}px`
    }

    return ReactDOM.createPortal(
      (
        <ul
          className="GuidelinesDropdown__items GuidelinesDropdown__element"
          style={style}
        >
          <li
            key={0}
            className={classnames(
              'GuidelinesDropdown__item',
              { 'GuidelinesDropdown__item-selectable': !noGuidelineSelected }
            )}
            onClick={handleSetNoGuidelineOption}
          >
            <div className="GuidelinesDropdown__item-name">
              No guideline
            </div>
            {noGuidelineSelected && (
              <Checkmark />
            )}
          </li>
          {guidelinesList}
        </ul>
      ),
      document.getElementById('site-container')!
    )
  }

  const renderTooltipGuideline = () => {
    const guidelineButtonBounds = guidelineButtonRef.current ? guidelineButtonRef.current.getBoundingClientRect() : { x: 0, y: 0, width: 0, height: 0 }
    const tooltipY = guidelineButtonBounds.y + guidelineButtonBounds.height + TOOLTIP_OFFSET
    const tooltipX = guidelineButtonBounds.x  - 0.5 * TOOLTIP_WIDTH + 0.5 * guidelineButtonBounds.width

    const style = {
      top: `${tooltipY}px`,
      left: `${tooltipX}px`
    }

    return ReactDOM.createPortal(
      (
        <div
          className="SetupProjectPopup__tooltip"
          style={style}
        >
          <Tooltip
            title="Apply a guideline"
            text="Use a predefined or a custom guideline to review your project. The AI will check the content for accuracy, tone, and formatting based on the selected guideline."
          />
        </div>
      ),
      document.getElementById('site-container')!
    )
  }

  return (
    <div className="GuidelinesDropdown GuidelinesDropdown__element">
      <div
        className={classnames(
          'SetupProjectPopup__button-add',
          { 'SetupProjectPopup__button-add-active': showGuidelinesDropdown },
          { 'SetupProjectPopup__button-add-selected': !noGuidelineSelected }
        )}
        onMouseEnter={onMouseEnterGuideline}
        onMouseLeave={onMouseLeaveGuideline}
        onClick={toggleGuidelinesDropdown}
        ref={guidelineButtonRef}
      >
        <AddGuideline />
      </div>
      {shouldShowTooltip && renderTooltipGuideline()}
      {showGuidelinesDropdown && renderGuidelinesDropdown()}
    </div>
  )
}

export default GuidelinesDropdown
